import React, { useRef, useEffect } from 'react';
import { t } from 'i18next';
import { Input, Button, TrashIcon } from '@rabbit/elements/shared-components';
import { toast } from 'react-toastify';
import { FieldArray, FormikProps } from 'formik';
import { AddEditVendableFormValuesShape } from '@rabbit/elements/shared-types';
import { SageFileUploader } from '../../upload-wrapper/SageFileUploader';
import {
  DocTypeShapeTypes,
  FileUploaderVariations,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import {
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { PlusIcon } from '@heroicons/react/24/solid';

interface SerialNumberReferenceProps {
  props: FormikProps<AddEditVendableFormValuesShape>;
  vendableId?: string;
  newVendableId?: string;
  activePremiumTenant: string;
  parsedSerialNumbers: string[];
}

export const SerialNumberReference: React.FC<SerialNumberReferenceProps> = ({
  props,
  vendableId,
  newVendableId,
  activePremiumTenant,
  parsedSerialNumbers,
}) => {
  // Function to compare alphanumeric strings
  const compareAlphanumeric = (a: string, b: string) => {
    // Natural sort comparison for alphanumeric strings
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Function to check if a serial number is within a range
  const isSerialNumberInRange = (
    serialNumber: string,
    range: { start: string; end: string }
  ) => {
    if (!range.start || !range.end) return false;
    return (
      compareAlphanumeric(serialNumber, range.start) >= 0 &&
      compareAlphanumeric(serialNumber, range.end) <= 0
    );
  };

  // Function to check if a serial number already exists in the list
  const isSerialNumberDuplicate = (
    serialNumber: string,
    serialNumbers: string[]
  ) => {
    return serialNumbers.includes(serialNumber);
  };

  // Function to check if a range overlaps with individual serial numbers
  const doesRangeOverlapWithSerialNumbers = (
    range: { start: string; end: string },
    serialNumbers: string[]
  ) => {
    if (!range.start || !range.end) return false;
    return serialNumbers.some((sn) => isSerialNumberInRange(sn, range));
  };

  // Function to check if two ranges overlap
  const doRangesOverlap = (
    range1: { start: string; end: string },
    range2: { start: string; end: string }
  ) => {
    if (!range1.start || !range1.end || !range2.start || !range2.end)
      return false;

    // Check if range1 starts within range2
    const range1StartsInRange2 =
      compareAlphanumeric(range1.start, range2.start) >= 0 &&
      compareAlphanumeric(range1.start, range2.end) <= 0;

    // Check if range1 ends within range2
    const range1EndsInRange2 =
      compareAlphanumeric(range1.end, range2.start) >= 0 &&
      compareAlphanumeric(range1.end, range2.end) <= 0;

    // Check if range2 is completely within range1
    const range2WithinRange1 =
      compareAlphanumeric(range2.start, range1.start) >= 0 &&
      compareAlphanumeric(range2.end, range1.end) <= 0;

    return range1StartsInRange2 || range1EndsInRange2 || range2WithinRange1;
  };

  // Function to check if a range overlaps with any existing ranges
  const doesRangeOverlapWithExistingRanges = (
    range: { start: string; end: string },
    ranges: { start: string; end: string }[],
    currentIndex: number
  ) => {
    if (!range.start || !range.end) return false;

    return ranges.some((existingRange, index) => {
      // Skip comparing with itself
      if (index === currentIndex) return false;
      return doRangesOverlap(range, existingRange);
    });
  };

  return (
    <div className="mt-4 rounded-lg border border-gray-200 p-4">
      <h3 className="font-nunito text-md mb-4">{t('general.serialNumbers')}</h3>

      {/* Individual Serial Numbers */}
      <div className="mb-4">
        <h4 className="font-nunito mb-2">
          {t('general.addIndividualSerialNumbers')}
        </h4>
        <div className="mb-2 flex gap-2">
          <Input
            type="text"
            name="newSerialNumber"
            settings={{
              placeholder: t('general.enterSerialNumber'),
              value: props.values.newSerialNumber || '',
            }}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
              if (sanitized !== e.target.value) {
                // overwrite the displayed value if non-alphanumeric chars were typed
                e.target.value = sanitized;
              }
              props.setFieldValue('newSerialNumber', sanitized);
            }}
          />
          <Button
            kind="primary"
            type="button"
            icon={<PlusIcon />}
            children={t('general.add')}
            onClick={() => {
              if (props.values.newSerialNumber) {
                // Ensure serialNumbers is an array
                const currentSerialNumbers = props.values.serialNumbers || [];
                const newSerialNumber = props.values.newSerialNumber;

                props.setFieldValue('serialNumbers', [
                  ...currentSerialNumbers,
                  newSerialNumber,
                ]);
                props.setFieldValue('newSerialNumber', '');
              }
            }}
          />
        </div>

        {/* Display added serial numbers */}
        {props.values.serialNumbers &&
          props.values.serialNumbers.length > 0 && (
            <div className="mt-2">
              <div className="font-nunito mb-1 text-sm">
                {t('general.addedSerialNumbers')}:
              </div>
              <div className="flex flex-wrap gap-2">
                {props.values.serialNumbers.map((serialNumber, index) => (
                  <div
                    key={index}
                    className="flex items-center rounded bg-gray-100 px-2 py-1"
                  >
                    <span className="mr-2">{serialNumber}</span>
                    <button
                      type="button"
                      className="text-red-500"
                      onClick={() => {
                        // Ensure serialNumbers is an array
                        const currentSerialNumbers =
                          props.values.serialNumbers || [];
                        const newSerialNumbers = [...currentSerialNumbers];
                        newSerialNumbers.splice(index, 1);
                        props.setFieldValue('serialNumbers', newSerialNumbers);
                      }}
                    >
                      <TrashIcon onClick={() => {}} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>

      {/* Serial Number Ranges */}
      <div className="mb-4">
        <h4 className="font-nunito mb-2">{t('general.serialNumberRanges')}</h4>

        <FieldArray name="serialNumberRanges">
          {({ push, remove }) => (
            <div>
              {props.values.serialNumberRanges &&
                props.values.serialNumberRanges.length > 0 && (
                  <div className="mb-4">
                    {props.values.serialNumberRanges.map((range, index) => (
                      <div
                        key={index}
                        className="mb-4 flex flex-col border-b border-gray-200 pb-4 last:border-b-0"
                      >
                        <div className="flex items-center justify-between">
                          <h5 className="font-nunito text-sm font-semibold">
                            {t('general.range')} #{index + 1}
                          </h5>
                          <button
                            type="button"
                            className="text-red-500"
                            onClick={() => remove(index)}
                          >
                            <TrashIcon onClick={() => {}} className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <Input
                            type="text"
                            label={t('general.startSerialNumber')}
                            name={`serialNumberRanges.${index}.start`}
                            settings={{
                              placeholder: t('general.startSerialNumber'),
                              onFocus: () => {
                                console.log('Focus on start serial number');
                              },
                            }}
                            onChange={(e) => {
                              const sanitized = e.target.value.replace(
                                /[^a-zA-Z0-9]/g,
                                ''
                              );
                              if (sanitized !== e.target.value) {
                                e.target.value = sanitized;
                              }
                              props.setFieldValue(
                                `serialNumberRanges.${index}.start`,
                                sanitized
                              );
                            }}
                            onBlur={(e) => {
                              const start = e.target.value;
                              const end =
                                props.values.serialNumberRanges?.[index]?.end;
                              console.log(
                                'onBlur triggered for startSerialNumber',
                                start,
                                end
                              );
                            }}
                          />
                          <Input
                            type="text"
                            label={t('general.endSerialNumber')}
                            name={`serialNumberRanges.${index}.end`}
                            settings={{
                              placeholder: t('general.endSerialNumber'),
                            }}
                            onChange={(e) => {
                              const sanitized = e.target.value.replace(
                                /[^a-zA-Z0-9]/g,
                                ''
                              );
                              if (sanitized !== e.target.value) {
                                e.target.value = sanitized;
                              }
                              props.setFieldValue(
                                `serialNumberRanges.${index}.end`,
                                sanitized
                              );
                            }}
                            onBlur={(e) => {
                              const end = e.target.value;
                              const start =
                                props.values.serialNumberRanges?.[index]?.start;
                              console.log(
                                'onBlur triggered for endSerialNumber',
                                start,
                                end
                              );
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              <Button
                kind="outline"
                type="button"
                onClick={() => {
                  push({
                    start: '',
                    end: '',
                  });
                }}
              >
                {t('general.addRange')}
              </Button>
            </div>
          )}
        </FieldArray>
      </div>
    </div>
  );
};

export default SerialNumberReference;
